<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger'
import { AutoComplete, type AutoCompleteChangeEvent, DropDownList } from '@progress/kendo-vue-dropdowns'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import type { NumericDropdownOption } from '@/types/DropdownTypes'
import type { DropdownOption } from '@/types/DropdownTypes'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import { GetLocatorConsultantsByZip, GetLocatorConsultantsKnowAll } from '@/services/LocatorService'
import { GetConsultantFromLocalStorageOrApi } from '@/services/ConsultantService'
import { ConsultantGet } from '@/api/Consultant'
import { GetCountryCode, GetLoginUrl, GetKitCheckoutUrl, GetHomeOfficeBeeNumber, GetBeeNumberSite } from '@/stores/SettingsStore'
import type { IConsultantLocator } from '@/types/LocatorTypes'
import { SetIsLoading } from '@/services/AoeHelpers'
import AoeConsultantLocatorDetail from './AoeConsultantLocatorDetail.vue'
import { useCookies } from 'vue3-cookies'
import { type DropDownListChangeEvent } from '@progress/kendo-vue-dropdowns'


import { searchIcon } from '@progress/kendo-svg-icons'
import { PayeeAddToCart, GetCurrentCartFromLocalStorage } from '@/services/CartService'
import AoeAccountChoicePopup from './AoeAccountChoicePopup.vue'
import { trackConsultantSelected, trackSelectStarterKit } from '@/analytics/index'
import { buildMatomoItemsFromCart } from '@/analytics/matomoItemBuilder'


const props = defineProps<{
  kitordertypeid: string
}>()

const emit = defineEmits(["closePopup", "consultantSelected"]);

const { t } = useI18n()
const logger = useLogger()
const { cookies } = useCookies()
setStoreLogger(logger)

const countryCode = ref('')
const selectedCountry = ref('')
const consultantByName = ref(true)
const consultantByLocation = ref(false)
const selectedConsultant = ref('')
const selectedConsultantData = ref<IConsultantLocator | null>(null)
const showConsultantInfo = ref(false)
const showAccountChoice = ref(false)
const txtZipCode = ref('')
const directSignup = ref(false)
const directSignupError = ref(false)
const locatorConsultants = ref<IConsultantLocator[]>([])
const mapLocatorConsultants = ref<IConsultantLocator[]>([])
const searchText = ref('')
const currentCartIdToken = ref<string>('')
const searchRadius = ref<number>(25)
const hasSearched = ref(false)

const radiusOptions = computed<NumericDropdownOption[]>(() => [
  { value: 5 },
  { value: 25 },
  { value: 50 },
  { value: 100 }
])

const countryOptions: DropdownOption[] = [
  { value: 'DE', labelKey: 'countries.germany' },
  { value: 'AT', labelKey: 'countries.austria' },
  { value: 'FR', labelKey: 'countries.france' }
]

const countryOptionsWithLabels = computed(() => {
  return countryOptions.map((opt: DropdownOption) => ({
    value: opt.value,
    label: t(opt.labelKey)
  }))
})

GetCountryCode().then((cc: string) => {
  countryCode.value = cc
  selectedCountry.value = cc
})

const localizedCountryName = computed(() => {
  const code = selectedCountry.value.toUpperCase()
  if (code === 'DE') return 'Deutschland'
  if (code === 'AT') return 'Österreich'
  if (code === 'FR') return 'France'
  return ''
})

logger.debug('AoeCartPopupConsultantLocator.vue')

async function selectedConsultantHandler(consultant: IConsultantLocator, override?: boolean) {
  selectedConsultant.value = String(consultant.beeNumber)
  selectedConsultantData.value = consultant
  searchText.value = consultant.name
  showConsultantInfo.value = true

  const { cookies } = useCookies()
  if (!override){

    // Store the cart ID token
    currentCartIdToken.value = cookies.get('AoeCartIdToken')



    // Add the payee to the cart
    await PayeeAddToCart({
      aoeCartIdToken: currentCartIdToken.value,
      beeNumberPayee: consultant.beeNumber
    })
  }
  trackConsultantSelected(consultant.businessName || consultant.name, consultant.beeNumber)
  //window.location.href = window.location.href
}

function validateInput(value: string | null | undefined) {
  return Boolean(value && value.trim().length > 0)
}

async function changeConsultantTextHandler(event: AutoCompleteChangeEvent) {
  consultantByName.value = true
  const searchValue = event.value || ''
  searchText.value = searchValue
  logger.debug('changeConsultantTextHandler', { searchValue })

  if (event.event?.currentTarget?.className === 'k-clear-value') {
    logger.debug('clearConsultantTextHandler')
    selectedConsultant.value = ''
    selectedConsultantData.value = null
    locatorConsultants.value = []
    searchText.value = ''
    return
  }

  if (searchValue.length >= 3) {
    SetIsLoading(true)
    try {
      // Check if search value contains a space
      if (searchValue.indexOf(' ') > 0) {
        // Split by space - first part is firstName, second part is lastName
        const names = searchValue.split(' ')
        const firstName = names[0]
        const lastName = names.slice(1).join(' ') // Handle multiple spaces/names

        logger.debug('Searching consultants by full name', { firstName, lastName })

        // Make a single API call with both firstName and lastName
        const response = await GetLocatorConsultantsKnowAll({
          firstName: firstName,
          lastName: lastName
        })

        logger.debug('GetLocatorConsultants response', { response })
        locatorConsultants.value = response?.items || []
      } else {
        // No space - search by first name OR last name
        logger.debug('Searching consultants by first name and last name', { searchValue })

        // Make two parallel API calls - one for first name, one for last name
        const [firstNameResponse, lastNameResponse] = await Promise.all([
          GetLocatorConsultantsKnowAll({
            firstName: searchValue,
            lastName: null
          }),
          GetLocatorConsultantsKnowAll({
            firstName: null,
            lastName: searchValue
          })
        ])

        logger.debug('GetLocatorConsultants responses', { firstNameResponse, lastNameResponse })

        // Combine results from both searches
        const firstNameResults = firstNameResponse?.items || []
        const lastNameResults = lastNameResponse?.items || []

        // Create a Map to deduplicate by beeNumber
        const consultantMap = new Map<number, IConsultantLocator>()

        // Add all first name results
        firstNameResults.forEach(consultant => {
          consultantMap.set(consultant.beeNumber, consultant)
        })

        // Add all last name results (will not duplicate if beeNumber already exists)
        lastNameResults.forEach(consultant => {
          consultantMap.set(consultant.beeNumber, consultant)
        })

        // Convert Map back to array
        locatorConsultants.value = Array.from(consultantMap.values())

        logger.debug('Combined and deduplicated consultants', {
          firstNameCount: firstNameResults.length,
          lastNameCount: lastNameResults.length,
          totalUnique: locatorConsultants.value.length,
          consultants: locatorConsultants.value
        })
      }
    } catch (error) {
      console.error('Error fetching consultants:', error)
      locatorConsultants.value = []
    } finally {
      SetIsLoading(false)
    }
  } else {
    locatorConsultants.value = []
  }
}

function radioBtnChangeNameHandler() {
  consultantByName.value = true
  consultantByLocation.value = false
}

function radioBtnChangeLocationHandler() {
  consultantByName.value = false
  consultantByLocation.value = true
}

async function btnConfirmHandler() {
  if (validateInput(selectedConsultant.value)) {
    cookies.set('selectedConsultant', selectedConsultant.value)
    if (selectedConsultant.value) {
      // Get kit category code from URL params if available
      const urlParams = new URLSearchParams(window.location.search)
      const kitCategoryCode = urlParams.get('kitCategoryCode') || ''

      // Build checkout URL with kit parameters
      let returnUrl = await GetKitCheckoutUrl()
      if (props.kitordertypeid) {
        returnUrl += `?kitOrderTypeId=${encodeURIComponent(props.kitordertypeid)}`
        if (kitCategoryCode) {
          returnUrl += `&kitCategoryCode=${encodeURIComponent(kitCategoryCode)}`
        }
        const beeNumberSite = await GetBeeNumberSite()
        if (String(beeNumberSite) !== selectedConsultant.value) {
          returnUrl += `&newrsbnu=${encodeURIComponent(selectedConsultant.value)}`
        }

      }

      window.location.href =
      (await GetLoginUrl()) + '?returnUrl=' + encodeURIComponent(returnUrl)
    }
  }
}

function handleAccountChoiceClose() {
  showAccountChoice.value = false
}

function resetForm() {
  selectedConsultant.value = ''
  selectedConsultantData.value = null
  showConsultantInfo.value = false
  searchText.value = ''
  txtZipCode.value = ''
  locatorConsultants.value = []
  mapLocatorConsultants.value = []
  hasSearched.value = false
  consultantByName.value = true
  consultantByLocation.value = false
  directSignup.value = false
  directSignupError.value = false
}

function closePopupHandler() {
  resetForm()
  emit('closePopup')
}

function chooseDifferentConsultant() {
  showConsultantInfo.value = false
  selectedConsultant.value = ''
  selectedConsultantData.value = null
  searchText.value = ''
  locatorConsultants.value = []
  directSignup.value = false
      localStorage.setItem('directSignupUsed', 'false');
}

function handleRadiusChange(e: DropDownListChangeEvent) {
  const newValue = e.value?.value !== undefined ? e.value.value : e.value
  searchRadius.value = Number(newValue)
  if (txtZipCode.value) {
    searchClickHandler()
  }
}

async function searchClickHandler() {
  logger.debug('searchClickHandler')

  if (txtZipCode.value && txtZipCode.value.trim().length > 0) {
    SetIsLoading(true)
    hasSearched.value = false
    mapLocatorConsultants.value = []

    const zip = txtZipCode.value.trim()

    try {
      const response = await GetLocatorConsultantsByZip({
        postalCode: zip,
        countryCode: selectedCountry.value,
        distance: searchRadius.value
      })

      const consultants = Array.isArray(response.items) ? response.items : []
      mapLocatorConsultants.value = consultants
      hasSearched.value = true
    } catch (error) {
      console.error('[AoeKitRecruiterSelection] Error in searchClickHandler', error)
      hasSearched.value = true
    } finally {
      SetIsLoading(false)
    }
  }
}


async function preSelectConsultantByBeeNumber(beeNumber: number) {
  selectedConsultant.value = String(beeNumber)
  showConsultantInfo.value = true
  const consultant = await GetConsultantFromLocalStorageOrApi(beeNumber)
  if (consultant) {
    selectedConsultantData.value = {
      beeNumber: consultant.beeNumber,
      name: `${consultant.firstName} ${consultant.lastName}`,
      photoUrl: consultant.photoUrl,
      businessName: '',
      city: consultant.city,
      region: consultant.region,
      postalCode: consultant.shippingAddress?.postalCode || '',
      fluentIn: '',
      aoeCartIdToken: currentCartIdToken.value,
      latitude: undefined,
      longitude: undefined
    }
  }
}

onMounted(async () => {
  const siteCountryCode = await GetCountryCode()
  countryCode.value = siteCountryCode
  selectedCountry.value = siteCountryCode

  // Track select starter kit event on load since we redirected after selecting kit
  const cart = GetCurrentCartFromLocalStorage()
  if (cart && cart.items && cart.items.length > 0) {
    const matomoItems = await buildMatomoItemsFromCart(cart, props.kitordertypeid)
    trackSelectStarterKit(matomoItems, cart.total ?? 0, cart.currencyId || 'EUR')
  }

  // Initialize direct signup tracking if not already set
  if (!localStorage.getItem('directSignupUsed')) {
    localStorage.setItem('directSignupUsed', 'false');
  }

  // Check for existing cart ID token in cookies
  const aoeCartIdToken = cookies.get('AoeCartIdToken')
  if (aoeCartIdToken) {
    currentCartIdToken.value = aoeCartIdToken
  }
  logger.debug('AoeCartIdToken from cookies:', currentCartIdToken.value)

  const [homeOfficeBeeNum, siteBeeNumber] = await Promise.all([
    GetHomeOfficeBeeNumber(),
    GetBeeNumberSite()
  ])
  logger.debug('[KitRecruiterSelection] Site bee number:', siteBeeNumber, typeof siteBeeNumber, '| Home office bee number:', homeOfficeBeeNum, typeof homeOfficeBeeNum)

  if (siteBeeNumber && Number(siteBeeNumber) !== Number(homeOfficeBeeNum)) {
    logger.debug('[KitRecruiterSelection] Replicated site detected — pre-selecting consultant', { siteBeeNumber })
    await preSelectConsultantByBeeNumber(siteBeeNumber)
  } else {
    logger.debug('[KitRecruiterSelection] Not a replicated site — showing normal search')
  }
})

watch(directSignup, async (newValue) => {
  if (newValue) {
    directSignupError.value = false;

    // Track direct signup usage in localStorage
    localStorage.setItem('directSignupUsed', 'true');

    const beeNumber = await GetHomeOfficeBeeNumber() || 1;

    try {
      SetIsLoading(true);
      const consultant = await ConsultantGet(beeNumber);
      if (consultant) {

        // Add the payee to the cart
        await PayeeAddToCart({
          aoeCartIdToken: currentCartIdToken.value,
          beeNumberPayee: beeNumber
        });

        selectedConsultant.value = String(beeNumber);
        showConsultantInfo.value = true;

        selectedConsultantData.value = {
          beeNumber: consultant.item.beeNumber,
          name: consultant.item.firstName + ' ' + consultant.item.lastName,
          photoUrl: consultant.item.photoUrl || '',
          businessName: 'Pampered Chef Home Office',
          city: consultant.item.city || '',
          region: consultant.item.region,
          postalCode: consultant.item.shippingAddress?.postalCode || '',
          fluentIn: '',  // Not available in IAoeConsultant
          aoeCartIdToken: currentCartIdToken.value,
          latitude: undefined,
          longitude: undefined
        };

        trackConsultantSelected('Pampered Chef Home Office', consultant.item.beeNumber)
      } else {
        throw new Error(t('errors.noConsultantDataReceived'))
      }
    } catch (error) {
      console.error('Error fetching home office consultant:', error);
      directSignupError.value = true;
      directSignup.value = false; // Reset checkbox
    } finally {
      SetIsLoading(false);
    }
  }
})
</script>

<template>
  <div class="consultant-locator-container kit-recruiter-selection">
    <AoeAccountChoicePopup
      v-if="showAccountChoice && selectedConsultant"
      :visible="showAccountChoice"
      @close="handleAccountChoiceClose"
    />
    <div v-if="!showConsultantInfo" class="consultant-locator-header">
      <div class="header-header">
        <div>{{ t('consultantLocator.welcomeHeader') }}</div>
        <h4>{{ t('consultantLocator.teamJoiningHeader') }}</h4>
      </div>
      <div v-if="!showConsultantInfo" class="search-options">
        <div class="pic-logo">
          <img src="https://www.pamperedchef.com/consultant-agreement/assets/images/tpc-logo.svg" />
        </div>
        <p class="search-by-text">{{ t('consultantLocator.searchByText') }}</p>

        <div class="search-tabs">
          <button
            :class="['search-tab', { active: consultantByName }]"
            @click="radioBtnChangeNameHandler"
          >{{ t('consultantLocator.nameTab') }}</button>
          <button
            :class="['search-tab', { active: consultantByLocation }]"
            @click="radioBtnChangeLocationHandler"
          >{{ t('consultantLocator.locationTab') }}</button>
        </div>
      </div>

      <div v-if="consultantByName && !selectedConsultant" class="search-section mt-2 px-md-3">
        <div class="search-input-container">
          <AutoComplete
            class="search-input"
            :data-items="locatorConsultants"
            :placeholder="t('consultantLocator.enterConsultantName')"
            :text-field="'name'"
            :value="searchText"
            :item-render="AoeConsultantLocatorDetail"
            @selected-consultant="selectedConsultantHandler"
            @change="changeConsultantTextHandler"
          />
          <span class="search-icon">🔍</span>
        </div>
      </div>

      <div v-if="consultantByLocation && !selectedConsultant" class="search-section mt-2 px-md-3">
        <div class="search-input-container">
          <DropDownList
            :data-items="countryOptionsWithLabels"
            :text-field="'label'"
            :data-item-key="'value'"
            :value="countryOptionsWithLabels.find((opt: any) => opt.value === selectedCountry)"
            @change="(e: any) => selectedCountry = e.value.value"
            class="country-dropdown foo-bar"
          />
          <KTextBox
            wrapper-class="zip-code-input"
            :placeholder="t('consultantLocator.enterZipCode')"
            v-model="txtZipCode"
            @keyup.enter="searchClickHandler"
          />
          <KButton
            class="search-button"
            :svg-icon="searchIcon"
            :fill-mode="'clear'"
            @click="searchClickHandler"
          ></KButton>
        </div>
        <div class="radius-selector-container">
          <label class="radius-label">{{ t('consultantLocator.searchRadius') }}</label>
          <DropDownList
            :data-items="radiusOptions"
            :text-field="'value'"
            :data-item-key="'value'"
            :value="radiusOptions.find(opt => opt.value === searchRadius)"
            @change="handleRadiusChange"
            class="radius-dropdown"
          />
          <span class="radius-unit">{{ t('consultantLocator.kilometers') }}</span>
        </div>

        <div v-if="hasSearched && mapLocatorConsultants.length === 0" class="no-results-message">
          {{ t('consultantLocator.noConsultantsFound') }}
        </div>

      <div class="consultants-list" v-if="mapLocatorConsultants.length > 0">
        <h4>{{ t('consultantLocator.consultantsFound') }}</h4>
        <div
          v-for="consultant in mapLocatorConsultants"
          :key="consultant.beeNumber"
          :class="['consultant-item', { selected: String(consultant.beeNumber) === selectedConsultant }]"
        >
          <img
            class="consultant-photo"
            :src="consultant.photoUrl"
            :alt="consultant.name"
          />
          <div class="consultant-details">
            <div class="consultant-name">{{ consultant.name }}</div>
            <div class="consultant-business">{{ consultant.businessName }}</div>
            <div class="consultant-location">{{ localizedCountryName }}</div>
          </div>
          <button
            class="select-button"
            @click="selectedConsultantHandler(consultant)"
          >
            {{ t('cart.Select') }}
          </button>
        </div>
      </div>
      </div>

      <!-- Always show these sections at the bottom -->
      <div class="direct-signup-section px-md-3 mt-2">
        <label class="checkbox-label">
          <input type="checkbox" class="k-checkbox" v-model="directSignup" >
          <span class="checkbox-text">{{ t('consultantLocator.directSignupText') }}</span>
        </label>
        <div v-if="directSignupError" class="error-message">
          {{ t('consultantLocator.directSignupError') }}
        </div>
      </div>

      <div class="info-section">
        <h4>{{ t('consultantLocator.whyJoinConsultantQuestion') }}</h4>
        <p>{{ t('consultantLocator.consultantTeamInfo') }}</p>
        <p>{{ t('consultantLocator.experienceInfo') }}</p>
      </div>

    </div>


    <div v-if="showConsultantInfo" class="selected-consultant-info">
      <div class="consultant-profile">
        <img
          class="consultant-photo"
          :src="selectedConsultantData?.photoUrl"
          :alt="selectedConsultantData?.name"
        />
        <div class="consultant-details">
          <h3 class="consultant-name">{{ selectedConsultantData?.name }}</h3>
          <p class="consultant-location">{{ localizedCountryName }}</p>
          <button class="change-consultant-btn" @click="chooseDifferentConsultant">
            {{ t('consultantLocator.selectDifferentConsultant') }}
          </button>
        </div>
      </div>
      <button class="select-continue" @click="btnConfirmHandler">
        {{ t('consultantLocator.selectAndContinue') }}
      </button>
      <div class="info-section">
        <h4>{{ t('consultantLocator.whyJoinConsultantPersonal') }}</h4>
        <p>{{ t('consultantLocator.consultantTeamInfo') }}</p>
        <p>{{ t('consultantLocator.experienceInfo') }}</p>
      </div>
    </div>

    <div v-if="!showConsultantInfo" class="action-buttons d-none">
      <button class="cancel-button" @click="closePopupHandler">{{ t('cart.Cancel') }}</button>
      <button
        class="confirm-button"
        @click="btnConfirmHandler"
        :disabled="!selectedConsultantData && !directSignup"
      >{{ t('cart.Confirm') }}</button>
    </div>
  </div>
</template>

<style lang="scss">
.consultant-locator-container {
  max-width: 550px;
  margin: 0 auto;

  font-family: Arial, sans-serif;
  border:1px solid rgba(black, 0.5);
  background-color: white;
}

.consultant-locator-header {
  text-align: center;

}

.consultant-locator-header h2 {
  @include typography-h2;
  color: #333;
  margin-bottom: 0.5rem;
}

.consultant-locator-header h3 {
  @include typography-h2;
  color: #444;

}

.search-options {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.pic-logo {
  width: 80px;
  height: 80px;

  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

}

.pic-text {
  @include typography-h2;
  @include font-weight-bold;
}

.pic-i {
  font-style: italic;
}

.search-by-text {
  @include typography-h4;
  margin: 0;
}

.search-tabs {
  display: flex;
  gap: 1rem;

}

.search-tab {
  padding: 0.5rem 2rem;
  border: none;
  background: none;
  @include typography-body-large;
  border-bottom: 2px solid #cccccc;
  cursor: pointer;
  transition: all 0.3s;
}

.search-tab.active {
  @include font-weight-bold;
  border-bottom-color: #000;
}

.search-section {
  max-width: 600px;
  margin: 0 auto;
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: $spacing-2;

  .country-dropdown {
    width: 120px;
    min-width: 120px;
  }

  .search-input {
    flex: 1;
  }
}

.search-input {
  width: 100%;
  padding: 0.8rem;
  padding-right: 40px;
  @include typography-body;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}


.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-text {
  @include typography-body;
  color: #666;
}

.info-section {
  background: var(--vt-c-white-soft);
  padding: 1.5rem;
  border-radius: $radius-md;
}

.info-section h4 {
  @include typography-body-large;
  margin-bottom: 1rem;
  color: #333;
}

.info-section p {
  @include typography-body;
  color: #666;
  margin-bottom: 1rem;
}

.locatorItem {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: $spacing-2-5;
  padding: $spacing-2-5;
  border: 1px solid #cccccc;
  margin-bottom: 5px;
  border-radius: $radius-sm;
}

.locatorItem img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 50%;
}

.locatorItem .details {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.locatorItem .details h3 {
  @include typography-h4;
  margin: 0;
}

.locatorItem .details p {
  margin: $spacing-1 0;
  color: #666;
}

.map-container {
  margin-bottom: 2rem;
  border-radius: $radius-md;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.search-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-text);
  cursor: pointer;
  pointer-events: auto;
  padding: $spacing-2;
  z-index: 2;
}

.marker-info-card {
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.marker-info-photo {
  width: 56px;
  height: 56px;
  border-radius: $avatar-radius;
  object-fit: cover;
}

.marker-info-name {
  @include typography-body;
  @include font-weight-semibold;
  color: var(--color-text);
}

.marker-info-business,
.marker-info-location {
  @include typography-small;
  color: var(--color-text);
}

.marker-select-button {
  align-self: flex-start;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
  border-top: 1px solid #e9ecef;
}

.cancel-button,
.confirm-button {
  @include typography-body;
  padding: 0.8rem 2rem;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-button {
  background: none;
  border: 1px solid #cccccc;
  color: #666;
}

.cancel-button:hover {
  background: var(--vt-c-white-soft);
}

.confirm-button {
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border: none;
  color: white;
}

.confirm-button:hover {
  background: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}
.header-header{
  background-color: var(--vt-c-teal)21;
  padding: 1.5rem;
  text-align: center;
}

.header-header div {
  @include typography-h4;
  color: #666;
  margin-bottom: 0.5rem;
}

.header-header h4 {
  @include typography-h3;
  color: #333;
  margin: 0;
}

.selected-consultant-info {
  padding: 2rem;
}

.consultant-profile {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.consultant-profile .consultant-photo {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
}

.consultant-profile .consultant-details {
  flex: 1;
}

.consultant-profile .consultant-name {
  @include typography-h2;
  color: #333;
  margin: 0 0 0.5rem 0;
}

.consultant-profile .consultant-location {
  @include typography-h4;
  color: #666;
  margin: 0 0 1rem 0;
}

.change-consultant {
  color: #007bff;
  background: none;
  @include typography-body;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}
.change-consultant-btn
{
  border: none;
    background: white;
    color: var(--vt-c-teal-dark);
    padding-left: unset;

    :hover{
      text-decoration: underline;
    }
}

.select-continue {
  width: 100%;
  padding: 1rem;
  background-color: #2B6B77;
  color: white;
  @include typography-body-large;
  border: none;
  border-radius: $radius-sm;
  cursor: pointer;
  margin-bottom: 2rem;
}

.select-continue:hover {
  background-color: #1e4b54;
}

.consultant-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  margin-bottom: 1rem;
  transition: all 0.3s;
  flex-wrap: wrap;
  justify-content: center;
}


.consultant-item-row {
  display: flex;
  align-items: center;
  padding: $spacing-2;
  cursor: pointer;
  transition: background-color 0.2s;
}

.consultant-item-row:hover {
  background-color: var(--vt-c-white-soft);
}

.consultant-item-row .consultant-photo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
  object-fit: cover;
}

.consultant-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.consultant-item.selected {
  background-color: var(--vt-c-white-soft);
  border-color: #007bff;
}

.consultant-photo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
}

.consultant-details {
  flex: 1;
}

.consultant-name {
  @include typography-body-large;
  @include font-weight-bold;
  margin-bottom: 0.25rem;
}

.consultant-business {
  color: #666;
  margin-bottom: 0.25rem;
}

.consultant-location {
  @include typography-body;
  color: #888;
  margin-bottom:unset;
}

.select-button {
  padding: 0.5rem 1rem;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.3s;
}

.select-button:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}

.consultants-list {
  margin: 2rem 0;
}

.consultants-list h4 {
  @include typography-h4;
  margin-bottom: 1rem;
  color: #333;
}

.popupCloseButton {
  @include typography-h3;
  padding: 0;
}
.content.component:has(.kit-recruiter-selection){
  background-color: #fefefe !important;
}

.error-message {
  @include typography-body;
  color: #dc3545;
  margin-top: 0.5rem;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.no-results-message {
  text-align: center;
  padding: 1.5rem;
  margin: 1rem 0;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  @include typography-body;
  border-radius: $radius-md;
  color: #856404;
}

.radius-selector-container {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  margin-top: $spacing-4;
  margin-bottom: $spacing-2;
}

.radius-label {
  @include typography-body;
  font-weight: 500;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.radius-dropdown {
  width: 80px;
  min-width: 80px;
}

.radius-unit {
  @include typography-body;
  color: #666;
  margin-left: $spacing-1;
}

@media (max-width: 768px) {
  .kit-recruiter-selection {
    padding: $spacing-3;

    .search-input-container {

      .zip-code-input {
        max-width: 150px;
      }

      .search-button {
        right: -10px;
      }
    }
  }
}

</style>
